import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { jsPDF } from "jspdf";
import {
  ArrowLeft, FileText, Download, Loader2, BookOpen, Info,
  Calculator, Sparkles, Save, Check, FileDown,
} from "lucide-react";
import TopNav from "../components/TopNav";
import api from "../lib/api";
import { TRIM_SIZES, PAPER_TYPES } from "../components/cover/BookSetupModal";
import { buildDocxBlob } from "../lib/docxFrontMatter";

/**
 * Front Matter Generator
 *
 * - GET /api/frontmatter/{id} hydrates the form when ?id is in the URL.
 * - POST /api/frontmatter creates a new project; PUT updates.
 * - Status auto-flips from "draft" to "final" once an ISBN is filled in
 *   (handled server-side based on the isbn field).
 *
 * Exports both PDF and DOCX. DOCX comes in two styles ("rich" mirrors the
 * PDF layout; "simple" is left-aligned and easier to restyle in Word).
 */

function gutterForPages(pp) {
  if (pp <= 150) return 0.375;
  if (pp <= 300) return 0.5;
  if (pp <= 500) return 0.625;
  if (pp <= 700) return 0.75;
  return 0.875;
}

const DEFAULT_FORM = {
  book_title: "",
  subtitle: "",
  author: "",
  publisher: "",
  copyright_year: new Date().getFullYear(),
  edition: "First edition",
  isbn: "",
  dedication: "",
  acknowledgments: "",
  rights_notice:
    "All rights reserved. No part of this book may be reproduced or transmitted in any form or by any means without the prior written permission of the publisher, except in the case of brief quotations embodied in critical reviews.",
  disclaimer: "",
  include_half_title: true,
  include_title: true,
  include_copyright: true,
  include_dedication: false,
  include_acknowledgments: false,
  trim_size: "6x9",
  target_pages: "",
};

const FrontMatterGenerator = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [status, setStatus] = useState("draft");
  const [savedId, setSavedId] = useState(projectId || null);
  const [loading, setLoading] = useState(!!projectId);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [generating, setGenerating] = useState("");
  const [coverLink, setCoverLink] = useState("");
  const [covers, setCovers] = useState([]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const trim = useMemo(
    () => TRIM_SIZES.find((t) => t.value === form.trim_size) || TRIM_SIZES[0],
    [form.trim_size]
  );

  /* ---------------------- Load / list ---------------------- */
  useEffect(() => {
    let mounted = true;
    // Always load the user's cover projects for the "Load from cover" linker.
    api.get("/covers").then((r) => {
      if (mounted) setCovers(r.data || []);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!projectId) return;
    let mounted = true;
    setLoading(true);
    api.get(`/frontmatter/${projectId}`).then((r) => {
      if (!mounted) return;
      const d = r.data;
      setForm({
        book_title: d.book_title || "",
        subtitle: d.subtitle || "",
        author: d.author || "",
        publisher: d.publisher || "",
        copyright_year: d.copyright_year || new Date().getFullYear(),
        edition: d.edition || "First edition",
        isbn: d.isbn || "",
        dedication: d.dedication || "",
        acknowledgments: d.acknowledgments || "",
        rights_notice: d.rights_notice || DEFAULT_FORM.rights_notice,
        disclaimer: d.disclaimer || "",
        include_half_title: d.include_half_title ?? true,
        include_title: d.include_title ?? true,
        include_copyright: d.include_copyright ?? true,
        include_dedication: d.include_dedication ?? false,
        include_acknowledgments: d.include_acknowledgments ?? false,
        trim_size: d.trim_size || "6x9",
        target_pages: d.target_pages || "",
      });
      setStatus(d.status || "draft");
      setSavedId(d.id);
      setLastSavedAt(new Date(d.updated_at));
    }).catch(() => {
      alert("Could not load that front matter project.");
      navigate("/frontmatter", { replace: true });
    }).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [projectId, navigate]);

  /* ---------------------- Cross-link from cover ---------------------- */
  const loadFromCover = (cid) => {
    setCoverLink(cid);
    if (!cid) return;
    const c = covers.find((x) => x.id === cid);
    if (!c) return;
    setForm((f) => ({
      ...f,
      book_title: c.book_title || f.book_title,
      subtitle: c.subtitle || f.subtitle,
      author: c.author || f.author,
      trim_size: c.trim_size || f.trim_size,
    }));
  };

  /* ---------------------- Page list ---------------------- */
  const pages = useMemo(() => {
    const out = [];
    if (form.include_half_title) {
      out.push({ type: "half_title" });
      out.push({ type: "blank" });
    }
    if (form.include_title) {
      out.push({ type: "title" });
      out.push({ type: "copyright_or_blank" });
    }
    if (form.include_copyright && !form.include_title) {
      out.push({ type: "copyright" });
      out.push({ type: "blank" });
    }
    if (form.include_dedication && form.dedication.trim()) {
      out.push({ type: "dedication" });
      out.push({ type: "blank" });
    }
    if (form.include_acknowledgments && form.acknowledgments.trim()) {
      out.push({ type: "acknowledgments" });
      if (out.length % 2 !== 0) out.push({ type: "blank" });
    }
    return out;
  }, [form]);

  const padding = useMemo(() => {
    const target = parseInt(form.target_pages);
    if (!target) return 0;
    const need = target - pages.length;
    return need > 0 ? need : 0;
  }, [form.target_pages, pages.length]);

  const totalPages = pages.length + padding;
  const canGenerate = !!form.book_title.trim();

  /* ---------------------- Save / persist ---------------------- */
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        copyright_year: parseInt(form.copyright_year) || new Date().getFullYear(),
        target_pages: form.target_pages ? parseInt(form.target_pages) : null,
      };
      const isUpdate = !!savedId;
      const { data } = isUpdate
        ? await api.put(`/frontmatter/${savedId}`, payload)
        : await api.post(`/frontmatter`, payload);
      setStatus(data.status || "draft");
      setSavedId(data.id);
      setLastSavedAt(new Date());
      if (!isUpdate) {
        navigate(`/frontmatter/${data.id}`, { replace: true });
      }
    } catch (e) {
      alert("Save failed: " + (e?.response?.data?.detail || e.message || "unknown error"));
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------- PDF generation ---------------------- */
  const generatePdf = async () => {
    setGenerating("pdf");
    try {
      const pdf = new jsPDF({
        orientation: trim.h >= trim.w ? "portrait" : "landscape",
        unit: "in",
        format: [trim.w, trim.h],
        compress: false,
      });
      const margin = 0.75;
      const gutter = gutterForPages(totalPages);

      const drawPage = (type, pageIndex) => {
        const isRecto = pageIndex % 2 === 0;
        const innerMargin = isRecto ? gutter : margin;
        const outerMargin = isRecto ? margin : gutter;
        const usableW = trim.w - innerMargin - outerMargin;
        const usableH = trim.h - margin * 2;
        const leftX = innerMargin;
        const centerX = innerMargin + usableW / 2;
        const topY = margin;
        if (type === "blank" || type === "padding") return;
        if (type === "copyright_or_blank") { drawPage("copyright", pageIndex); return; }
        if (type === "half_title") {
          pdf.setFont("times", "normal");
          pdf.setFontSize(28);
          const title = (form.book_title || "Untitled").toUpperCase();
          const wrapped = pdf.splitTextToSize(title, usableW);
          const blockH = wrapped.length * 0.45;
          const y = topY + usableH * 0.45 - blockH / 2;
          wrapped.forEach((line, i) => pdf.text(line, centerX, y + i * 0.45, { align: "center" }));
          return;
        }
        if (type === "title") {
          pdf.setFont("times", "normal");
          pdf.setFontSize(36);
          const title = form.book_title || "Untitled";
          const wrappedTitle = pdf.splitTextToSize(title, usableW);
          let y = topY + usableH * 0.28;
          wrappedTitle.forEach((line, i) => pdf.text(line, centerX, y + i * 0.55, { align: "center" }));
          y += wrappedTitle.length * 0.55 + 0.35;
          if (form.subtitle) {
            pdf.setFontSize(16);
            pdf.setFont("times", "italic");
            const wrappedSub = pdf.splitTextToSize(form.subtitle, usableW);
            wrappedSub.forEach((line, i) => pdf.text(line, centerX, y + i * 0.25, { align: "center" }));
            y += wrappedSub.length * 0.25;
          }
          if (form.author) {
            pdf.setFontSize(14);
            pdf.setFont("times", "normal");
            pdf.text(form.author, centerX, topY + usableH * 0.82, { align: "center" });
          }
          if (form.publisher) {
            pdf.setFontSize(11);
            pdf.text(form.publisher, centerX, topY + usableH * 0.92, { align: "center" });
          }
          return;
        }
        if (type === "copyright") {
          pdf.setFont("times", "normal");
          pdf.setFontSize(10);
          const lines = [];
          const title = form.book_title || "Untitled";
          lines.push(`${title}${form.subtitle ? `: ${form.subtitle}` : ""}`);
          lines.push("");
          const author = form.author || "the author";
          lines.push(`Copyright \u00a9 ${form.copyright_year} by ${author}.`);
          lines.push("");
          lines.push(form.rights_notice);
          lines.push("");
          if (form.isbn) lines.push(`ISBN: ${form.isbn}`);
          lines.push(form.edition);
          if (form.publisher) { lines.push(""); lines.push(`Published by ${form.publisher}`); }
          if (form.disclaimer) { lines.push(""); lines.push(form.disclaimer); }
          const bottomY = topY + usableH - 0.4;
          const wrapped = [];
          lines.forEach((l) => {
            if (l === "") { wrapped.push(""); return; }
            pdf.splitTextToSize(l, usableW).forEach((w) => wrapped.push(w));
          });
          const lineH = 0.16;
          const yStart = bottomY - wrapped.length * lineH;
          wrapped.forEach((line, i) => pdf.text(line, leftX, yStart + i * lineH, { align: "left" }));
          return;
        }
        if (type === "dedication") {
          pdf.setFont("times", "italic");
          pdf.setFontSize(14);
          const wrapped = pdf.splitTextToSize(form.dedication, usableW);
          const blockH = wrapped.length * 0.25;
          const y = topY + usableH * 0.4 - blockH / 2;
          wrapped.forEach((line, i) => pdf.text(line, centerX, y + i * 0.25, { align: "center" }));
          return;
        }
        if (type === "acknowledgments") {
          pdf.setFont("times", "bold");
          pdf.setFontSize(20);
          pdf.text("Acknowledgments", centerX, topY + 0.6, { align: "center" });
          pdf.setFont("times", "normal");
          pdf.setFontSize(11);
          const wrapped = pdf.splitTextToSize(form.acknowledgments, usableW);
          let y = topY + 1.4;
          wrapped.forEach((line, i) => pdf.text(line, leftX, y + i * 0.2, { align: "left" }));
          return;
        }
      };

      const all = [...pages, ...Array(padding).fill({ type: "padding" })];
      all.forEach((p, i) => {
        if (i > 0) pdf.addPage([trim.w, trim.h], trim.h >= trim.w ? "portrait" : "landscape");
        drawPage(p.type, i);
      });
      const fileBase = (form.book_title || "front-matter")
        .replace(/[^a-zA-Z0-9-_]/g, "-").toLowerCase();
      pdf.save(`${fileBase}-front-matter.pdf`);
    } catch (err) {
      console.error("PDF export failed", err);
      alert("PDF export failed: " + (err?.message || "unknown error"));
    } finally {
      setGenerating("");
    }
  };

  /* ---------------------- DOCX generation ---------------------- */
  const generateDocx = async (style) => {
    setGenerating(`docx-${style}`);
    try {
      const blob = await buildDocxBlob({ form, pages, padding, trim, style });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const fileBase = (form.book_title || "front-matter")
        .replace(/[^a-zA-Z0-9-_]/g, "-").toLowerCase();
      a.href = url;
      a.download = `${fileBase}-front-matter-${style}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("DOCX export failed", err);
      alert("DOCX export failed: " + (err?.message || "unknown error"));
    } finally {
      setGenerating("");
    }
  };

  /* ---------------------- Render ---------------------- */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <TopNav />
        <div className="flex items-center justify-center h-[60vh] text-sm text-[#5F5E5A] gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading project…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <TopNav />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-sm text-[#5F5E5A] hover:text-[#2C2C2A] inline-flex items-center gap-1.5 mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="flex items-end justify-between gap-4 mb-2 flex-wrap">
          <div>
            <h1
              className="text-3xl tracking-tight text-[#2C2C2A]"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
            >
              Front Matter Generator
            </h1>
            <p className="text-sm text-[#5F5E5A] mt-1 max-w-2xl">
              Save your draft, upload to KDP, come back with the ISBN, and re-export as PDF + DOCX.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {savedId && (
              <span
                data-testid="fm-status"
                className={`px-2.5 py-1.5 rounded-md text-[11px] font-medium border ${
                  status === "final"
                    ? "bg-[#E8F6F0] border-[#1D9E75]/40 text-[#1D9E75]"
                    : "bg-[#FFF4E5] border-[#E4D6BA] text-[#7A5C0E]"
                }`}
              >
                {status === "final" ? "Final · ISBN set" : "Draft · ISBN pending"}
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !canGenerate}
              data-testid="fm-save-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] border border-[#534AB7] text-[#534AB7] text-sm font-medium hover:bg-[#534AB7]/5 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (lastSavedAt ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />)}
              {saving ? "Saving…" : (savedId ? "Save changes" : "Save project")}
            </button>
            {savedId && (
              <button
                onClick={() => navigate(`/book/${savedId}`)}
                data-testid="fm-book-overview-btn"
                title="Open the single-page snapshot of every artefact for this book"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] border border-[#D3D1C7] text-[#2C2C2A] text-sm font-medium hover:bg-[#F1EFE8]"
              >
                <BookOpen className="w-4 h-4" /> Book overview
              </button>
            )}
            {savedId ? (
              <button
                onClick={() => navigate(`/cover/new?from=${savedId}`)}
                data-testid="fm-create-cover-btn"
                title="Open the Cover Creator pre-filled with this book's details"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489]"
              >
                <BookOpen className="w-4 h-4" /> Create matching cover →
              </button>
            ) : (
              <button
                onClick={async () => {
                  // Save first (so the cover can be pre-filled from this
                  // project) then jump. If save fails — e.g. validation —
                  // surface that via the existing save flow.
                  if (canGenerate) {
                    await handleSave();
                  }
                  // Always still jump to the cover creator even if save
                  // hasn't happened — user will fill in setup manually.
                  navigate("/cover/new");
                }}
                data-testid="fm-create-cover-btn-blank"
                title="Open the Cover Creator (saves this project first if filled in)"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489]"
              >
                <BookOpen className="w-4 h-4" /> Create matching cover →
              </button>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8">
          {/* ---- Form ---- */}
          <div className="space-y-6">
            {covers.length > 0 && (
              <div className="bg-[#F1EFE8]/60 border border-[#D3D1C7] rounded-[10px] p-4 flex items-center gap-3" data-testid="fm-cover-link">
                <FileText className="w-4 h-4 text-[#534AB7]" />
                <label className="text-xs text-[#5F5E5A] uppercase tracking-wider">
                  Pre-fill from cover project
                </label>
                <select
                  value={coverLink}
                  onChange={(e) => loadFromCover(e.target.value)}
                  data-testid="fm-load-cover-select"
                  className="flex-1 px-2 py-1.5 rounded-md bg-white border border-[#D3D1C7] text-xs text-[#2C2C2A] focus:outline-none focus:border-[#534AB7]"
                >
                  <option value="">— choose a cover —</option>
                  {covers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.book_title || "Untitled"} · {c.trim_size}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <Section title="Book details" icon={BookOpen}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Book title" required>
                  <input
                    value={form.book_title}
                    onChange={(e) => update("book_title", e.target.value)}
                    data-testid="fm-title"
                    className={inputCls}
                    placeholder="The Year I Wrote 12 Books"
                  />
                </Field>
                <Field label="Subtitle (optional)">
                  <input
                    value={form.subtitle}
                    onChange={(e) => update("subtitle", e.target.value)}
                    data-testid="fm-subtitle"
                    className={inputCls}
                    placeholder="An indie publisher's playbook"
                  />
                </Field>
                <Field label="Author">
                  <input
                    value={form.author}
                    onChange={(e) => update("author", e.target.value)}
                    data-testid="fm-author"
                    className={inputCls}
                    placeholder="Sam Henderson"
                  />
                </Field>
                <Field label="Publisher (optional)">
                  <input
                    value={form.publisher}
                    onChange={(e) => update("publisher", e.target.value)}
                    data-testid="fm-publisher"
                    className={inputCls}
                    placeholder="Henderson Press"
                  />
                </Field>
              </div>
              <Field label="Trim size">
                <select
                  value={form.trim_size}
                  onChange={(e) => update("trim_size", e.target.value)}
                  data-testid="fm-trim"
                  className={inputCls}
                >
                  {TRIM_SIZES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </Field>
            </Section>

            <Section title="Copyright page" icon={FileText}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Copyright year">
                  <input
                    type="number"
                    min="1900"
                    max="2099"
                    value={form.copyright_year}
                    onChange={(e) => update("copyright_year", e.target.value)}
                    data-testid="fm-copyright-year"
                    className={inputCls}
                  />
                </Field>
                <Field label="Edition">
                  <input
                    value={form.edition}
                    onChange={(e) => update("edition", e.target.value)}
                    data-testid="fm-edition"
                    className={inputCls}
                    placeholder="First edition"
                  />
                </Field>
                <Field label="ISBN (fill after KDP)">
                  <input
                    value={form.isbn}
                    onChange={(e) => update("isbn", e.target.value)}
                    data-testid="fm-isbn"
                    className={inputCls}
                    placeholder="978-0-00-000000-0"
                  />
                </Field>
              </div>
              <Field label="Rights notice">
                <textarea
                  value={form.rights_notice}
                  onChange={(e) => update("rights_notice", e.target.value)}
                  data-testid="fm-rights"
                  rows={3}
                  className={inputCls + " resize-y leading-snug"}
                />
              </Field>
              <Field label="Additional disclaimer (optional)">
                <textarea
                  value={form.disclaimer}
                  onChange={(e) => update("disclaimer", e.target.value)}
                  data-testid="fm-disclaimer"
                  rows={2}
                  className={inputCls + " resize-y leading-snug"}
                  placeholder="This is a work of fiction. Names, characters, places…"
                />
              </Field>
            </Section>

            <Section title="Optional pages" icon={Sparkles}>
              <ToggleRow
                checked={form.include_dedication}
                onChange={(v) => update("include_dedication", v)}
                label="Dedication page"
                hint="Centred italic text. Common: 'For Mum.' or 'To my readers.'"
                testid="fm-include-dedication"
              />
              {form.include_dedication && (
                <Field label="Dedication">
                  <textarea
                    value={form.dedication}
                    onChange={(e) => update("dedication", e.target.value)}
                    rows={3}
                    data-testid="fm-dedication"
                    className={inputCls + " resize-y"}
                    placeholder="For Mum, who read every draft."
                  />
                </Field>
              )}
              <ToggleRow
                checked={form.include_acknowledgments}
                onChange={(v) => update("include_acknowledgments", v)}
                label="Acknowledgments page"
                hint="Thank-yous to editors, beta readers, family, etc."
                testid="fm-include-acknowledgments"
              />
              {form.include_acknowledgments && (
                <Field label="Acknowledgments">
                  <textarea
                    value={form.acknowledgments}
                    onChange={(e) => update("acknowledgments", e.target.value)}
                    rows={5}
                    data-testid="fm-acknowledgments"
                    className={inputCls + " resize-y leading-snug"}
                    placeholder="Thank you to my editor…"
                  />
                </Field>
              )}
            </Section>

            <Section title="Page padding" icon={Calculator}>
              <Field label="Target total pages (optional)">
                <input
                  type="number"
                  min="0"
                  max="900"
                  value={form.target_pages}
                  onChange={(e) => update("target_pages", e.target.value)}
                  data-testid="fm-target-pages"
                  className={inputCls}
                  placeholder="72"
                />
              </Field>
              <p className="text-[11px] text-[#5F5E5A] leading-snug">
                If you set a target, we'll add blank pages at the end to hit it exactly —
                useful for clearing KDP page-count minimums (e.g. 72 for colour paper).
              </p>
            </Section>
          </div>

          {/* ---- Preview / Generate column ---- */}
          <aside className="lg:sticky lg:top-6 self-start space-y-4">
            <div className="bg-white border border-[#D3D1C7] rounded-[12px] p-6" data-testid="fm-summary">
              <div className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A] mb-3">
                Generated preview
              </div>
              <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                {pages.length === 0 ? (
                  <div className="text-sm text-[#5F5E5A] italic" data-testid="fm-empty">
                    Toggle at least one page on the left.
                  </div>
                ) : (
                  pages.map((p, i) => <PageRow key={i} index={i + 1} type={p.type} />)
                )}
                {padding > 0 && (
                  <PageRow
                    index={pages.length + 1}
                    type="padding"
                    label={`+ ${padding} blank padding page${padding === 1 ? "" : "s"}`}
                  />
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-[#D3D1C7] text-sm text-[#5F5E5A] flex items-center justify-between">
                <span>Total pages</span>
                <span className="text-[#2C2C2A] font-medium tabular-nums" data-testid="fm-total-pages">
                  {totalPages}
                </span>
              </div>
              <div className="mt-1 text-sm text-[#5F5E5A] flex items-center justify-between">
                <span>Trim size</span>
                <span className="text-[#2C2C2A] tabular-nums">{trim.w}″ × {trim.h}″</span>
              </div>
              <div className="mt-1 text-sm text-[#5F5E5A] flex items-center justify-between">
                <span>Gutter (auto)</span>
                <span className="text-[#2C2C2A] tabular-nums">{gutterForPages(totalPages).toFixed(3)}″</span>
              </div>

              <div className="mt-5 space-y-2">
                <button
                  onClick={generatePdf}
                  disabled={!canGenerate || !!generating}
                  data-testid="fm-generate-pdf-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50"
                >
                  {generating === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Generate &amp; download PDF
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => generateDocx("rich")}
                    disabled={!canGenerate || !!generating}
                    data-testid="fm-generate-docx-rich-btn"
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-[8px] border border-[#D3D1C7] text-[#2C2C2A] text-xs font-medium hover:bg-[#F1EFE8] disabled:opacity-50"
                  >
                    {generating === "docx-rich" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                    DOCX — rich
                  </button>
                  <button
                    onClick={() => generateDocx("simple")}
                    disabled={!canGenerate || !!generating}
                    data-testid="fm-generate-docx-simple-btn"
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-[8px] border border-[#D3D1C7] text-[#2C2C2A] text-xs font-medium hover:bg-[#F1EFE8] disabled:opacity-50"
                  >
                    {generating === "docx-simple" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                    DOCX — simple
                  </button>
                </div>
              </div>
              {!canGenerate && (
                <p className="mt-2 text-[11px] text-[#BA7517] flex items-center gap-1.5">
                  <Info className="w-3 h-3" /> Book title is required.
                </p>
              )}
            </div>

            <div className="bg-[#F1EFE8]/60 border border-[#D3D1C7] rounded-[10px] p-4 text-xs text-[#5F5E5A] leading-relaxed">
              <strong className="block text-[#2C2C2A] mb-1">ISBN round-trip</strong>
              <ol className="list-decimal pl-4 space-y-1">
                <li>Fill in everything except ISBN, click <strong>Save project</strong>.</li>
                <li>Download the draft PDF, upload to KDP, complete the listing flow.</li>
                <li>KDP shows you the assigned ISBN on the next page — copy it.</li>
                <li>Reopen this project from <em>My Projects</em>, paste the ISBN, save, and re-export the final PDF + DOCX.</li>
              </ol>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-[#D3D1C7] rounded-[12px] p-6 space-y-4">
    <div className="flex items-center gap-2.5 pb-2 border-b border-[#D3D1C7]/70">
      <Icon className="w-4 h-4 text-[#534AB7]" strokeWidth={1.6} />
      <h2 className="text-sm uppercase tracking-[0.18em] text-[#5F5E5A] font-medium">{title}</h2>
    </div>
    {children}
  </div>
);

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-[#5F5E5A] uppercase tracking-wider mb-1.5">
      {label} {required && <span className="text-[#534AB7]">*</span>}
    </span>
    {children}
  </label>
);

const ToggleRow = ({ checked, onChange, label, hint, testid }) => (
  <label className="flex items-start gap-3 px-3 py-2.5 rounded-[8px] hover:bg-[#FAFAF9] cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={testid}
      className="mt-1 rounded border-[#D3D1C7] accent-[#534AB7]"
    />
    <div className="flex-1">
      <div className="text-sm text-[#2C2C2A]">{label}</div>
      <div className="text-[11px] text-[#5F5E5A] leading-snug">{hint}</div>
    </div>
  </label>
);

const PAGE_LABELS = {
  half_title: "Half-title page",
  title: "Title page",
  copyright: "Copyright page",
  copyright_or_blank: "Copyright page",
  dedication: "Dedication page",
  acknowledgments: "Acknowledgments",
  blank: "Blank page",
  padding: "Blank padding",
};

const PageRow = ({ index, type, label }) => (
  <div className="flex items-center gap-3 text-xs">
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#F1EFE8] text-[#5F5E5A] tabular-nums">
      {index}
    </span>
    <span className="text-[#2C2C2A] flex-1 truncate">
      {label || PAGE_LABELS[type] || type}
    </span>
  </div>
);

const inputCls =
  "w-full px-3 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-sm text-[#2C2C2A] focus:outline-none focus:border-[#534AB7] focus:ring-1 focus:ring-[#534AB7] placeholder:text-[#5F5E5A]/50";

export default FrontMatterGenerator;
export { PAPER_TYPES };
